import React, { useState, useEffect, useCallback } from 'react';
import { 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  TagIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import PromotionModal from '../../components/admin/PromotionModal';
import ConfirmModal from '../../components/admin/ConfirmModal';
import SuccessModal from '../../components/admin/SuccessModal';
import { apiService } from '../../services/api';

interface AdminPromotion {
  id: number;
  name: string;
  slug: string;
  description: string;
  type: 'percentage' | 'fixed_amount' | 'buy_x_get_y';
  value: number;
  minimum_amount?: number;
  maximum_discount?: number;
  start_date: string;
  end_date: string;
  usage_limit?: number;
  usage_limit_per_customer?: number;
  used_count: number;
  applies_to: 'all' | 'specific_products' | 'specific_categories' | 'specific_brands';
  conditions?: any;
  is_active: boolean;
  products_count: number;
  created_at: string;
}

interface Statistics {
  total: number;
  active: number;
  inactive: number;
  expired: number;
  upcoming: number;
  with_products: number;
}

const AdminPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<AdminPromotion[]>([]);
  const [statistics, setStatistics] = useState<Statistics>({
    total: 0,
    active: 0,
    inactive: 0,
    expired: 0,
    upcoming: 0,
    with_products: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(15);

  // Modal states
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<AdminPromotion | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
  const [confirmMessage, setConfirmMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Helper function to get promotion status
  const getPromotionStatus = (promotion: AdminPromotion): string => {
    if (!promotion.is_active) {
      return 'Inactive';
    }
    
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);
    
    if (startDate > now) {
      return 'Upcoming';
    }
    
    if (endDate < now) {
      return 'Expired';
    }
    
    return 'Active';
  };

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);

      const response = await apiService.get(`/admin/promotions?${params}`);
      
      if (response.success !== false) {
        setPromotions(response.data || response);
        setTotalPages(response.meta?.last_page || response.data?.meta?.last_page || 1);
      } else {
        console.error('Failed to fetch promotions:', response.message);
        setPromotions([]);
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, sortBy, sortOrder, searchTerm, statusFilter, typeFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await apiService.get('/admin/promotions/statistics');
      if (response.success !== false) {
        setStatistics(response.data || response);
      } else {
        console.error('Failed to fetch statistics:', response.message);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchStatistics();
  }, [fetchPromotions, fetchStatistics]);

  const handleCreatePromotion = () => {
    setSelectedPromotion(null);
    setModalMode('create');
    setIsPromotionModalOpen(true);
  };

  const handleView = async (id: number) => {
    try {
      const response = await apiService.get(`/admin/promotions/${id}`);
      if (response.success !== false) {
        setSelectedPromotion(response.data || response);
        setIsPromotionModalOpen(true);
        setModalMode('view');
      } else {
        console.error('Failed to fetch promotion:', response.message);
        alert('Failed to load promotion details');
      }
    } catch (error) {
      console.error('Error fetching promotion:', error);
      alert('Error loading promotion details');
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const response = await apiService.get(`/admin/promotions/${id}`);
      if (response.success !== false) {
        setSelectedPromotion(response.data || response);
        setIsPromotionModalOpen(true);
        setModalMode('edit');
      } else {
        console.error('Failed to fetch promotion:', response.message);
        alert('Failed to load promotion for editing');
      }
    } catch (error) {
      console.error('Error fetching promotion:', error);
      alert('Error loading promotion for editing');
    }
  };

  const handleDeletePromotion = (promotion: AdminPromotion) => {
    setConfirmMessage(`Are you sure you want to delete the promotion "${promotion.name}"? This action cannot be undone.`);
    setConfirmAction(() => async () => {
      try {
        const response = await apiService.delete(`/admin/promotions/${promotion.id}`);
        // Handle different response structures
        if (response && (response.success !== false || response.data?.success !== false)) {
          setSuccessMessage('Promotion deleted successfully!');
          setIsSuccessModalOpen(true);
          fetchPromotions();
          fetchStatistics();
        } else {
          const errorMessage = response?.message || response?.data?.message || 'Failed to delete promotion';
          console.error('Failed to delete promotion:', errorMessage);
          alert(`Failed to delete promotion: ${errorMessage}`);
        }
      } catch (error: any) {
        console.error('Error deleting promotion:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error deleting promotion';
        alert(`Error deleting promotion: ${errorMessage}`);
      }
    });
    setIsConfirmModalOpen(true);
  };

  const handleToggleStatus = async (promotion: AdminPromotion) => {
    try {
      const response = await apiService.put(`/admin/promotions/${promotion.id}/toggle-status`);
      if (response.success !== false) {
        fetchPromotions();
        fetchStatistics();
      } else {
        console.error('Failed to toggle promotion status:', response.message);
        alert('Failed to update promotion status');
      }
    } catch (error) {
      console.error('Error toggling promotion status:', error);
      alert('Error updating promotion status');
    }
  };

  const handleSavePromotion = async (promotionData: any) => {
    try {
      let response;
      if (modalMode === 'create') {
        response = await apiService.post('/admin/promotions', promotionData);
      } else {
        response = await apiService.put(`/admin/promotions/${selectedPromotion?.id}`, promotionData);
      }

      if (response.success !== false) {
        setSuccessMessage(`Promotion ${modalMode === 'create' ? 'created' : 'updated'} successfully!`);
        setIsSuccessModalOpen(true);
        setIsPromotionModalOpen(false);
        fetchPromotions();
        fetchStatistics();
      } else {
        console.error('Failed to save promotion:', response.message);
        alert(`Failed to ${modalMode === 'create' ? 'create' : 'update'} promotion: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error saving promotion:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Error ${modalMode === 'create' ? 'creating' : 'updating'} promotion: ${errorMessage}`);
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.get('/admin/promotions/export');
      if (response.success !== false) {
        const data = response.data || response;
        const csvContent = data.data ? data.data.map((row: any[]) => row.join(',')).join('\n') : '';
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'promotions_export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Failed to export promotions:', response.message);
        alert('Failed to export promotions');
      }
    } catch (error) {
      console.error('Error exporting promotions:', error);
      alert('Error exporting promotions');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Expired': 'bg-red-100 text-red-800',
      'Upcoming': 'bg-blue-100 text-blue-800',
      // Legacy support for lowercase
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'expired': 'bg-red-100 text-red-800',
      'upcoming': 'bg-blue-100 text-blue-800',
    };
    return badges[status as keyof typeof badges] || badges.Inactive;
  };

  const getTypeDisplay = (type: string) => {
    const types = {
      percentage: 'Percentage Discount',
      fixed_amount: 'Fixed Amount Discount',
      buy_x_get_y: 'Buy X Get Y',
      'percentage_discount': 'Percentage Discount',
      'fixed_amount_discount': 'Fixed Amount Discount',
      'buy_x_get_y_discount': 'Buy X Get Y',
    };
    return types[type as keyof typeof types] || 'Discount Promotion';
  };

  const formatValue = (type: string, value: number) => {
    if (type === 'percentage') {
      return `${value}%`;
    }
    return `${value.toLocaleString()} Ks`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Promotion Management</h1>
            <p className="text-gray-600 mt-1">Manage promotional offers and discounts</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleExport}
              className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handleCreatePromotion}
              className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Promotion</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TagIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <ChartBarIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <TagIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.inactive}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.expired}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.upcoming}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CurrencyDollarIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">With Products</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.with_products}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search promotions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
            <option value="upcoming">Upcoming</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
          >
            <option value="">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed_amount">Fixed Amount</option>
            <option value="buy_x_get_y">Buy X Get Y</option>
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="start_date-desc">Start Date (Latest)</option>
            <option value="end_date-desc">End Date (Latest)</option>
          </select>
        </div>
      </div>

      {/* Promotions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  Promotion
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Type & Value
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Duration
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Usage
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Products
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    Loading promotions...
                  </td>
                </tr>
              ) : promotions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No promotions found
                  </td>
                </tr>
              ) : (
                promotions.map((promotion) => (
                  <tr key={promotion.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 truncate">{promotion.name}</div>
                        <div className="text-sm text-gray-500 truncate max-w-48">{promotion.description}</div>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {getTypeDisplay(promotion.type)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatValue(promotion.type, promotion.value)}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="text-sm text-gray-900">
                        <div>{formatDate(promotion.start_date)}</div>
                        <div className="text-gray-500">to {formatDate(promotion.end_date)}</div>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="text-sm text-gray-900">
                        <div>{promotion.used_count}</div>
                        <div className="text-xs text-gray-500">
                          {promotion.usage_limit ? `/${promotion.usage_limit}` : '/∞'}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        {promotion.products_count}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(getPromotionStatus(promotion))}`}>
                        {getPromotionStatus(promotion)}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(promotion.id)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(promotion.id)}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(promotion)}
                          className={`p-1 rounded transition-colors ${
                            promotion.is_active
                              ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-100'
                              : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                          }`}
                          title={promotion.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <UsersIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePromotion(promotion)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {isPromotionModalOpen && (
        <PromotionModal
          isOpen={isPromotionModalOpen}
          onClose={() => setIsPromotionModalOpen(false)}
          promotion={selectedPromotion}
          mode={modalMode}
          onSave={handleSavePromotion}
        />
      )}

      {isConfirmModalOpen && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={() => {
            confirmAction();
            setIsConfirmModalOpen(false);
          }}
          onCancel={() => setIsConfirmModalOpen(false)}
          type="danger"
          confirmText="Delete"
        />
      )}

      {isSuccessModalOpen && (
        <SuccessModal
          message={successMessage}
          onClose={() => setIsSuccessModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminPromotions;
